import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import TrustBadge from "@/components/TrustBadge";
import ShareBahoiCard from "@/components/ShareBahoiCard";
import { useAuth } from "@/contexts/AuthContext";
import { CAUSE_COLORS, CAUSE_NAMES } from "@/lib/causes";
import { ArrowRight, Clock, Heart, Plus, CheckCircle2, Hourglass, RotateCcw, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [matchedOpps, setMatchedOpps] = useState([]);
  const [secondaryOpps, setSecondaryOpps] = useState([]);
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    api.get("/me/summary").then(({ data }) => setSummary(data));
    api.get("/me/signups").then(({ data }) => {
      setUpcoming(data.filter((s) => s.status === "signed_up" || s.status === "completed_pending"));
    });
    api.get("/me/trust").then(({ data }) => setTrust(data));
    if (user?.id && user?.matched_causes?.length) {
      api
        .get(`/opportunities?matched_only=true&user_id=${user.id}`)
        .then(({ data }) => {
          const primaryCause = user.primary_cause;
          const primaryList = data.filter((o) => o.cause === primaryCause).slice(0, 4);
          const secondaryList = data.filter((o) => o.cause !== primaryCause).slice(0, 3);
          setMatchedOpps(primaryList);
          setSecondaryOpps(secondaryList);
        });
    }
  }, [user?.id, user?.primary_cause, user?.matched_causes?.length]);

  const primary = user?.primary_cause;
  const primaryName = primary ? CAUSE_NAMES[primary] : null;

  return (
    <Layout>
      <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-5 md:px-8 py-12 fade-up">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Welcome / balance */}
          <div className="md:col-span-2 card-soft p-8 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[var(--cm-accent)]/30 blur-3xl" />
            <span className="overline text-[var(--cm-primary)]">Welcome back</span>
            <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
              Hi {user?.name?.split(" ")[0] || "friend"}.
            </h1>
            <div className="mt-6 flex flex-wrap items-end gap-6">
              <div>
                <span className="overline text-ink-soft">Your balance</span>
                <div className="mt-1 flex items-baseline gap-2" data-testid="dashboard-balance">
                  <span className="font-display font-bold text-5xl">
                    {Number(summary?.balance ?? user?.time_hours_balance ?? 0).toFixed(1)}
                  </span>
                  <span className="text-ink-soft font-semibold">Time Hours</span>
                </div>
              </div>
              {primary && (
                <div>
                  <span className="overline text-ink-soft">Matched cause</span>
                  <div
                    data-testid="dashboard-primary-cause"
                    className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-semibold"
                    style={{ background: CAUSE_COLORS[primary] }}
                  >
                    <Heart className="h-4 w-4" />
                    {primaryName}
                  </div>
                </div>
              )}
              {trust && (
                <div>
                  <span className="overline text-ink-soft">Your trust score</span>
                  <div className="mt-1">
                    <TrustBadge trust={trust} testId="dashboard-trust-badge" />
                  </div>
                </div>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/opportunities" data-testid="dashboard-find-opps" className="btn-primary inline-flex items-center gap-2">
                Find a shift <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/marketplace" data-testid="dashboard-go-market" className="btn-outline">
                Spend my hours
              </Link>
              {!user?.assessment_completed ? (
                <Link to="/assessment" data-testid="dashboard-take-quiz" className="btn-secondary">
                  Take the assessment
                </Link>
              ) : (
                <Link
                  to="/assessment"
                  data-testid="dashboard-retake-quiz"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-[var(--cm-primary)] px-3 py-2 transition-colors"
                  title="Retake the assessment"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Retake assessment
                </Link>
              )}
            </div>
          </div>

          <Stat
            testid="stat-earned"
            label="Hours earned"
            value={Number(summary?.total_earned || 0).toFixed(1)}
            icon={CheckCircle2}
            color="var(--cm-secondary)"
          />
          <Stat
            testid="stat-upcoming"
            label="Upcoming shifts"
            value={summary?.upcoming_count ?? 0}
            icon={Clock}
            color="var(--cm-primary)"
          />
          <Stat
            testid="stat-pending"
            label="Awaiting verification"
            value={summary?.pending_count ?? 0}
            icon={Hourglass}
            color="#B37A2C"
          />
          <Stat
            testid="stat-org-pending"
            label="To verify (you organize)"
            value={summary?.organizer_pending_count ?? 0}
            icon={CheckCircle2}
            color="#4D5E7A"
          />
        </div>

        <section className="mt-14 grid md:grid-cols-2 gap-8">
          {/* Upcoming */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-2xl tracking-tight">Your upcoming shifts</h2>
              <Link to="/my-volunteering" className="text-sm text-[var(--cm-primary)] font-semibold" data-testid="dashboard-see-all-volunteering">
                See all →
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="card-soft p-8 text-center" data-testid="dashboard-no-upcoming">
                <p className="text-ink-soft">No shifts signed up yet.</p>
                <Link to="/opportunities" className="btn-primary mt-4 inline-block text-sm">Browse opportunities</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.slice(0, 4).map((s) => (
                  <div key={s.id} className="card-soft p-5" data-testid={`upcoming-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-semibold">{s.opportunity_title}</h3>
                        <p className="text-xs text-ink-soft mt-1">
                          {CAUSE_NAMES[s.opportunity_cause]} · with {s.organizer_name}
                        </p>
                      </div>
                      <TimeBadge hours={s.opportunity_hours} size="sm" testId={`upcoming-hours-${s.id}`} />
                    </div>
                    <span className="pill mt-3" style={{ background: s.status === "completed_pending" ? "#FDF0D5" : "#fff" }}>
                      {s.status === "signed_up" ? "Signed up" : "Awaiting organizer verification"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Matched opportunities */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-2xl tracking-tight">
                {primary ? "Aligned with your cause" : "Browse opportunities"}
              </h2>
              <Link to="/opportunities" className="text-sm text-[var(--cm-primary)] font-semibold" data-testid="dashboard-see-all-opps">
                See all →
              </Link>
            </div>
            {matchedOpps.length === 0 ? (
              <div className="card-soft p-8 text-center" data-testid="dashboard-no-matched">
                <p className="text-ink-soft">Take the assessment to see matched opportunities.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {matchedOpps.map((o) => (
                  <Link
                    key={o.id}
                    to={`/opportunities/${o.id}`}
                    data-testid={`matched-opp-${o.id}`}
                    className="card-soft p-5 block hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-semibold leading-tight">{o.title}</h3>
                        <p className="text-xs text-ink-soft mt-1">
                          {o.cause_name} · {o.mode === "online" ? "Online" : o.location}
                        </p>
                      </div>
                      <TimeBadge hours={o.hours} size="sm" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {secondaryOpps.length > 0 && (
          <section className="mt-14" data-testid="dashboard-secondary-opps-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tight inline-flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[var(--cm-primary)]" />
                  You might also love these
                </h2>
                <p className="text-xs text-ink-soft mt-1">
                  Based on the other causes that resonated with you in the assessment.
                </p>
              </div>
              <Link to="/opportunities" className="text-sm text-[var(--cm-primary)] font-semibold whitespace-nowrap" data-testid="dashboard-see-all-secondary">
                Browse all →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {secondaryOpps.map((o) => (
                <Link
                  key={o.id}
                  to={`/opportunities/${o.id}`}
                  data-testid={`secondary-opp-${o.id}`}
                  className="card-soft p-5 block hover:-translate-y-0.5 transition-transform relative overflow-hidden"
                >
                  <span
                    className="absolute right-0 top-0 h-16 w-16 rounded-bl-3xl opacity-15"
                    style={{ background: CAUSE_COLORS[o.cause] || "#a05a2c" }}
                  />
                  <div className="relative">
                    <span
                      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ background: CAUSE_COLORS[o.cause] || "#a05a2c" }}
                    >
                      {o.cause_name}
                    </span>
                    <h3 className="font-display font-semibold leading-tight mt-3">{o.title}</h3>
                    <p className="text-xs text-ink-soft mt-1">
                      {o.mode === "online" ? "Online" : o.location} · {o.is_ongoing ? "Ongoing" : (o.date || "TBD")}
                    </p>
                    <div className="mt-3">
                      <TimeBadge hours={o.hours} size="sm" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14">
          <div
            data-testid="dashboard-host-banner"
            className="rounded-3xl bg-[var(--cm-secondary)] text-white px-8 md:px-12 py-10 md:py-12 grid md:grid-cols-2 gap-8 items-center"
          >
            <div>
              <span className="overline text-[var(--cm-accent)]">Host an opportunity</span>
              <h3 className="font-display font-bold text-2xl md:text-3xl mt-2 tracking-tight">
                Got a project? Bring your neighbors in.
              </h3>
              <p className="text-white/70 mt-3 max-w-md">
                Post an opportunity and verify volunteer hours — they spend them in the marketplace.
              </p>
            </div>
            <div className="md:justify-self-end flex gap-3">
              <Link to="/opportunities/new" data-testid="dashboard-create-opp-btn" className="btn-primary inline-flex items-center gap-2">
                <Plus className="h-4 w-4" /> Create opportunity
              </Link>
              <Link to="/marketplace/new" data-testid="dashboard-create-listing-btn" className="btn-outline border-white text-white hover:bg-white hover:text-[var(--cm-ink)]">
                Offer something
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <ShareBahoiCard userName={user?.name} />
        </section>
      </div>
    </Layout>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="card-soft p-6" data-testid={testid}>
      <span className="h-9 w-9 rounded-full grid place-items-center text-white" style={{ background: color }}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="mt-4">
        <div className="font-display font-bold text-3xl">{value}</div>
        <div className="text-xs uppercase tracking-widest text-ink-soft mt-1">{label}</div>
      </div>
    </div>
  );
}
